<template>
    <div class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('componentEditLink.title') }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="pathToInstallationEditComponents">{{ $t('componentEditLink.breadcrumb') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ $t('componentEditLink.title') }}</li>
                    </ol>
                </div>
            </div>

            <!-- Formulaire -->
            <form id="componentForm" name="componentForm" @submit.prevent="handleSubmit" class="pb-4">
                <div class="row">
                    <div class="col-xxl-8 col-xl-7 d-flex flex-column">
                        <div class="card flex-grow-1 mb-3">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('common.identification') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <!-- Colonne gauche : Image -->
                                    <div class="col-md-6 d-flex flex-column justify-content-center align-items-center text-center mb-2 mb-md-0">
                                        <img :src="hub.image || '/default-hub.png'" alt="Image du hub"
                                            class="img-fluid rounded shadow-sm mb-2"
                                            style="max-height: 200px; object-fit: contain;" />
                                    </div>

                                    <!-- Colonne droite : Infos -->
                                    <div class="col-md-6">
                                        <div class="row">
                                            <div class="mb-2 col-12">
                                                <label for="nom" class="form-label">{{ $t('componentEditLink.linkName') }}</label>
                                                <input type="text" class="form-control" id="nom" required maxlength="32" v-model="hub.label" />
                                            </div>

                                            <div class="mb-2 col-12">
                                                <label for="numero-serie" class="form-label">{{ $t('common.serialNumber') }}</label>
                                                <input type="text" class="form-control" id="numero-serie" v-model="hub.serial_number" readonly />
                                            </div>

                                            <div class="mb-2 col-12">
                                                <label for="versions" class="form-label">{{ $t('common.versions') }}</label>
                                                <input type="text" class="form-control" id="versions" v-model="hub.getVersions" readonly />
                                            </div>

                                            <div class="mb-2 col-12">
                                                <label for="warrantyStartDate" class="form-label">{{ $t('common.warrantyStart') }}</label>
                                                <input type="text" class="form-control" id="warranty-start-date" v-model="hub.getWarrantyStartDate" readonly />
                                            </div>

                                            <div v-if="hub.mac_address" class="mb-2 col-12">
                                                <label for="mac-address" class="form-label">{{ $t('common.macAddress') }}</label>
                                                <input type="text" class="form-control" id="mac-address" v-model="hub.mac_address" readonly />
                                            </div>

                                            <div class="mb-2 col-12">
                                                <label for="integrated-license" class="form-label">{{ $t('componentEditLink.integratedLicense') }}</label>
                                                <select id="integrated-license" ref="integratedLicenseSelect" class="form-select" v-model="integratedLicenseName" required>
                                                    <option value="" disabled>{{ $t('componentEditLink.integratedLicenseSelect') }}</option>
                                                    <option v-for="license in integratedLicenseOptions" :key="license.name" :value="license.name">{{ license.type }}</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card flex-shrink-0">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('componentEditHub.antiPassBackParams') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="col-mt-2">
                                        <div class="d-flex justify-content-between align-items-center bg-primary-lighten rounded py-1 px-2">
                                            <label class="form-check-label flex-grow-1 me-2" for="anti-pass-back">{{ $t('common.antiPassBack') }}</label>
                                            <input type="checkbox" id="anti-pass-back" v-model="hub.anti_pass_back" data-switch="bool" />
                                            <label for="anti-pass-back" :data-on-label="$t('common.yes')" :data-off-label="$t('common.no')"></label>
                                        </div>
                                    </div>
                                    <div v-if="hub.anti_pass_back" class="mb-3 col-mt-2">
                                        <div class="d-flex justify-content-between align-items-center bg-primary-lighten rounded py-1 px-2">
                                            <label class="form-check-label flex-grow-1 me-2" for="anti-time-back">{{ $t('common.antiTimeBack') }}</label>
                                            <input type="checkbox" id="anti-time-back" v-model="antiTimeBackToggle" data-switch="bool" />
                                            <label for="anti-time-back" :data-on-label="$t('common.yes')" :data-off-label="$t('common.no')"></label>
                                        </div>
                                    </div>
                                    <div v-if="antiTimeBackToggle" class="mb-3 col-mt-2">
                                        <TempoWithUnit :label="$t('componentEditHub.antiTimeBackDelay')"
                                            v-model:value="hub.anti_time_back" unit="minute(s)" :min="1" :max="120" :show-unit="false">
                                        </TempoWithUnit>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Licences activées sur le LINK -->
                        <div class="card flex-shrink-0">
                            <div class="card-header pb-0 d-flex align-items-center justify-content-between">
                                <h4 class="header-title">{{ $t('componentEditLink.licensesSection') }}</h4>
                                <button id="link-add-license" type="button" class="btn btn-sm btn-outline-primary" @click="addLicense">
                                    <i class="mdi mdi-plus me-1"></i>{{ $t('componentEditLink.licenseAdd') }}
                                </button>
                            </div>
                            <div class="card-body pt-2">
                                <p v-if="licenses.length === 0" class="text-center mb-0">{{ $t('componentEditLink.licensesEmpty') }}</p>
                                <table v-else id="link-licenses" class="table table-centered mb-0">
                                    <tbody>
                                        <tr v-for="license in licenses" :key="license.id">
                                            <td>
                                                <a href="javascript:void(0);" class="fw-bold text-primary license-key" @click="editLicense(license)">{{ license.unique_id }}</a>
                                                <span class="d-block license-type">{{ $t('componentEditLink.licenseLabel', { type: license.type }) }}</span>
                                                <span class="d-block font-13 fst-italic text-muted license-expiration">{{ $t('componentEditLink.licenseExpiresOn', { date: license.expirationDate }) }}</span>
                                            </td>
                                            <td class="table-action text-end">
                                                <a href="javascript:void(0);" class="text-secondary license-edit" :title="$t('common.edit')" :aria-label="$t('common.edit')" @click="editLicense(license)">
                                                    <i class="mdi mdi-pencil font-18"></i>
                                                </a>
                                                <a href="javascript:void(0);" class="text-danger license-delete" :title="$t('common.delete')" :aria-label="$t('common.delete')" @click="confirmModal.show(license)">
                                                    <i class="mdi mdi-trash-can font-18"></i>
                                                </a>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div class="col-xxl-4 col-xl-5">
                        <div class="card card-h-100">
                            <div class="card-header pb-0 d-flex align-items-center justify-content-between">
                                <h4 class="header-title">{{ $t('componentEditLink.composition') }}</h4>
                                <span v-if="hub.hub_state === 1" class="badge bg-success font-14 px-2">{{ $t('common.connected') }}</span>
                                <span v-else class="badge bg-danger font-14 px-2">{{ $t('common.disconnected') }}</span>
                            </div>
                            <div class="card-body ps-1">
                                <div ref="tree" class="component-treeview tree-components-lite">
                                    <ul>
                                        <li data-jstree='{ "icon" : "mdi mdi-server-network text-primary", "opened" : true }'>
                                            <div class="tree-row">
                                                <div id="hub_node" class="tree-cell component-name fw-bold text-primary">{{ hub.label }}</div>
                                                <div class="tree-cell component-action"></div>
                                            </div>
                                            <ul>
                                                <li v-for="reader in hub.readers" :key="reader.key(hub.id)" data-jstree='{ "icon" : "mdi mdi-key-wireless text-success" }'>
                                                    <div class="tree-row">
                                                        <div class="tree-cell component-name fw-bold text-dark">{{ reader.label }}</div>
                                                        <div class="tree-cell component-type">{{ reader.typeLabel }}</div>
                                                        <div class="tree-cell component-action">
                                                            <TreeButton :path="reader.path(facilityId, hub.id)" />
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Form actions -->
                <div class="form-actions-sticky d-flex justify-content-center align-items-center">
                    <router-link :to="pathToInstallationEditComponents" class="btn btn-outline-secondary me-2">{{ $t('common.back') }}</router-link>
                    <button type="button" class="btn btn-outline-primary me-2" @click="updateFirmware">{{ $t('componentEditHub.firmwareUpdate') }}</button>
                    <button type="button" class="btn btn-danger me-2" @click="replaceOrDeleteModal.show()">{{ $t('componentEditHub.replaceOrDelete') }}</button>
                    <button type="submit" class="btn btn-primary">{{ $t('common.save') }}</button>
                </div>
            </form>
        </div>
        <ReplaceOrDeleteHubModal ref="replaceOrDeleteModal" component-type="LINK" :action="handleReplaceOrDelete" />
        <FirmwareUpdateModal ref="firmwareUpdateModal" />
        <LicenseModal ref="licenseModal" />
        <ConfirmationModal ref="confirmModal" :title="$t('common.confirmDelete')" :question="$t('componentEditLink.licenseDeleteConfirm')" type="danger"
            :button1="$t('common.cancel')" :button2="$t('common.delete')" :action="deleteLicense" />
    </div>
</template>

<script>
import { computed, reactive, ref, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { useI18n } from 'vue-i18n';
import { showToast } from '@/services/toast';
import TempoWithUnit from '@/components/tempoWithUnit.vue';
import TreeButton from '@/components/treeButton';
import ReplaceOrDeleteHubModal from '@/modals/replaceOrDeleteHubModal';
import FirmwareUpdateModal from '@/modals/firmwareUpdateModal';
import LicenseModal from '@/modals/licenseModal.vue';
import ConfirmationModal from '@/modals/confirmationModal.vue';
import License from '@/models/license';
import $ from 'jquery';

export default {
    name: 'ComponentEditLink',
    components: {
        TreeButton,
        TempoWithUnit,
        ReplaceOrDeleteHubModal,
        FirmwareUpdateModal,
        LicenseModal,
        ConfirmationModal,
    },
    setup() {
        const userStore = useUserStore();
        const router = useRouter();
        const route = useRoute();
        const { t, locale } = useI18n();
        const facilityId = parseInt(route.query.facilityId);
        const hubId = parseInt(route.query.id);
        const storeHub = userStore.getHub(facilityId, hubId);
        const hub = reactive(storeHub.clone());

        // Integrated license, chosen among the available licenses and saved with the form, as in the app
        const integratedLicenseName = ref(storeHub.integrated_license?.name ?? '');
        const integratedLicenseSelect = ref(null);
        // The current license stays selectable even when it is not offered anymore
        const integratedLicenseOptions = computed(() => {
            const items = [...(userStore.availableLicenses || [])];
            const current = storeHub.integrated_license;
            if (current && !items.some(license => license.name === current.name)) items.push(current);
            return items;
        });

        // A license is required to save, with the message of the app
        const checkIntegratedLicense = () => {
            if (!integratedLicenseSelect.value) return;
            integratedLicenseSelect.value.setCustomValidity(integratedLicenseName.value ? '' : t('componentEditLink.integratedLicenseRequired'));
        };
        watch([integratedLicenseName, locale], checkIntegratedLicense, { flush: 'post' });

        const handleSubmit = async () => {
            try {
                const selected = integratedLicenseOptions.value.find(license => license.name === integratedLicenseName.value);
                if (selected) hub.integrated_license = License.constructFromObject({ name: selected.name, maximum: selected.maximum });
                await userStore.updateHub(facilityId, storeHub, hub);
                storeHub.update(hub);
                document.getElementById("hub_node").innerHTML = hub.label;
                showToast('success', t('common.updateSuccess'));
            } catch (error) {
                showToast('error', error);
            }
        };

        const pathToInstallationEditComponents = computed(() => {
            return `/installation-edit?id=${facilityId}&action=components`;
        });

        const replaceOrDeleteModal = ref(null);

        const handleReplaceOrDelete = async (reason) => {
            try {
                await userStore.deleteHub(facilityId, storeHub, reason);
                await router.push(pathToInstallationEditComponents.value);
                showToast('success', t('componentEditHub.deleteSuccess', { type: 'LINK' }));
            } catch (error) {
                showToast('error', error);
            }
        };

        // Firmware update of the device, driven by the modal (confirmation, order to the server, result after the reboot)
        const firmwareUpdateModal = ref(null);

        const updateFirmware = async () => {
            const device = hub.productName;
            const result = await firmwareUpdateModal.value.show(facilityId, storeHub);
            if (!result) {
                // Cancelled, or the error was displayed by the modal
                return;
            }
            if (result.version && result.version !== hub.software_version) {
                // Reflect the installed version in the page and in the facility's hub
                hub.software_version = result.version;
                storeHub.software_version = result.version;
            }
            if (result.upToDate) {
                showToast('success', t('componentEditHub.firmwareUpToDate', { device }));
            } else if (result.rebooted) {
                // The device rebooted and reported the new firmware to the server
                showToast('success', t('componentEditHub.firmwareUpdateRebooted', { device }));
            } else {
                showToast('success', t('componentEditHub.firmwareUpdateDone', { device }));
            }
        };

        const antiTimeBackToggle = computed({
            get() {
                return hub.anti_time_back !== 0;
            },
            set(val) {
                hub.anti_time_back = val ? 1 : 0;
            },
        });

        // Licenses of the LINK, managed through the API independently of the form, as in the app
        const licenses = computed(() => storeHub.licenses);
        const licenseModal = ref(null);
        const confirmModal = ref(null);

        // The types a license can take come from the check of its key by the dialog
        const addLicense = async () => {
            const added = await licenseModal.value.show(facilityId, storeHub, null);
            if (added) showToast('success', t('componentEditLink.licenseAdded'));
        };

        const editLicense = async (license) => {
            const updated = await licenseModal.value.show(facilityId, storeHub, license);
            if (updated) showToast('success', t('componentEditLink.licenseUpdated'));
        };

        const deleteLicense = async (license) => {
            try {
                await userStore.deleteLinkLicense(facilityId, storeHub, license);
                showToast('success', t('componentEditLink.licenseDeleted'));
            } catch (message) {
                showToast('error', message);
            }
        };

        const tree = ref(null);

        onMounted(() => {
            checkIntegratedLicense();

            // Needed at once for the integrated license
            userStore.loadAvailableLicenses().catch(message => showToast('error', message));

            $(tree.value).jstree();

            $(tree.value).on('click', '.tree-button', (event) => {
                const jsonURI = $(event.currentTarget).attr('data-url');
                const uri = JSON.parse(jsonURI);
                router.push({ path: uri.path, query: uri.params });
            });
        });

        return {
            tree,
            facilityId,
            hub,
            integratedLicenseName,
            integratedLicenseSelect,
            integratedLicenseOptions,
            pathToInstallationEditComponents,
            antiTimeBackToggle,
            handleSubmit,
            replaceOrDeleteModal,
            handleReplaceOrDelete,
            firmwareUpdateModal,
            updateFirmware,
            licenses,
            licenseModal,
            confirmModal,
            addLicense,
            editLicense,
            deleteLicense,
        };
    },
};
</script>
